/**
 * premium-animations.js
 * Premium animation layer for Minimog OS 2.0
 *
 * Initializes alongside the theme's existing animations.js.
 * Does not modify or conflict with the theme's m-scroll-trigger system.
 *
 * Modules:
 *  1. pa-* IntersectionObserver  — triggers .pa-animate on scroll-in
 *  2. Auto-apply                 — adds pa-* classes to un-animated elements
 *  3. Word Split Reveal          — wraps heading words in animated spans
 *  4. Button Ripple              — injects ripple span on pointerdown
 *  5. Cart Drawer Stagger        — staggers cart items when drawer opens
 *  6. Skeleton Loading           — shimmer on unloaded images
 *  7. SVG Path Draw              — stroke animation on .pa-draw-svg elements
 *  8. Theme Editor Support       — reinitializes on shopify:section:load
 */

(function () {
  'use strict';

  /* Exit early if user prefers reduced motion */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* ============================================================
     UTILITY
     ============================================================ */

  /**
   * Run callback when DOM is ready.
   * Works whether the script is deferred or not.
   */
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /**
   * Double rAF helper — ensures a class addition is painted
   * before triggering a CSS transition.
   */
  function nextFrame(fn) {
    requestAnimationFrame(() => requestAnimationFrame(fn));
  }

  /* ============================================================
     1. INTERSECTION OBSERVER — pa-* scroll classes
     ============================================================ */

  /**
   * Shared observer. Fires once per element.
   * Adds `.pa-animate` which triggers CSS transitions/animations.
   */
  const paObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const el = entry.target;

        /* Set --pa-i on direct children before animating parent */
        if (el.classList.contains('pa-stagger-children')) {
          Array.from(el.children).forEach((child, i) => {
            child.style.setProperty('--pa-i', i);
          });
        }

        /* Set --pa-i on word inner spans before animating heading */
        if (el.classList.contains('pa-word-reveal')) {
          el.querySelectorAll('.pa-word-inner').forEach((span, i) => {
            span.style.setProperty('--pa-i', i);
          });
        }

        /* Read SVG path lengths so CSS transition can animate them */
        if (el.classList.contains('pa-draw-svg')) {
          el.querySelectorAll('path, circle, polyline, line').forEach((shape) => {
            try {
              const len = shape.getTotalLength();
              shape.style.setProperty('--pa-path-length', len);
            } catch (_) {
              /* getTotalLength not available on all shapes — use CSS fallback */
            }
          });
        }

        /* Trigger the CSS state */
        nextFrame(() => el.classList.add('pa-animate'));

        paObserver.unobserve(el);
      });
    },
    {
      rootMargin: '0px 0px -60px 0px',
      threshold: 0.08,
    }
  );

  /**
   * Observe all pa-* elements within a given root.
   * Called on init and on section load.
   */
  function observePaElements(root) {
    root = root || document;
    const selectors = [
      '.pa-blur-reveal',
      '.pa-scale-reveal',
      '.pa-clip-reveal',
      '.pa-fade-up',
      '.pa-stagger-children',
      '.pa-word-reveal',
      '.pa-draw-svg',
    ].join(',');

    root.querySelectorAll(selectors).forEach((el) => {
      /* Skip if already animated */
      if (!el.classList.contains('pa-animate')) {
        paObserver.observe(el);
      }
    });
  }

  /* ============================================================
     2. AUTO-APPLY pa-* CLASSES TO EXISTING ELEMENTS
     Only targets elements that are NOT already managed by the
     theme's m-scroll-trigger system, so there is no double-animation.
     ============================================================ */

  function autoApplyClasses(root) {
    root = root || document;

    /* Footer column groups — stagger children */
    root
      .querySelectorAll('.m-footer__blocks, .m-footer__row')
      .forEach((el) => {
        if (!el.classList.contains('m-scroll-trigger') && !el.classList.contains('pa-stagger-children')) {
          el.classList.add('pa-stagger-children');
          paObserver.observe(el);
        }
      });

    /* Subheadings without existing scroll trigger */
    root
      .querySelectorAll('.m-section__subheading:not(.m-scroll-trigger)')
      .forEach((el) => {
        if (!el.classList.contains('pa-fade-up') && !el.classList.contains('pa-blur-reveal')) {
          el.classList.add('pa-fade-up');
          paObserver.observe(el);
        }
      });

    /* ── Custom Liquid & Custom Content ── */

    /* Mixed-font headings (.mixed-heading-custom) used on homepage & product page.
       Contains child <span> elements (Poppins + Cormorant spans) so we animate the
       wrapper as a whole unit rather than splitting individual words. */
    root.querySelectorAll('.mixed-heading-custom').forEach((el) => {
      if (!el.classList.contains('pa-blur-reveal')) {
        el.classList.add('pa-blur-reveal');
        paObserver.observe(el);
      }
    });

    /* Product page features list — stagger each .feat-item child */
    root.querySelectorAll('.feat-list').forEach((el) => {
      if (!el.classList.contains('pa-stagger-children')) {
        el.classList.add('pa-stagger-children');
        paObserver.observe(el);
      }
    });

    /* Product page benefits bar — stagger each .benefit-item child */
    root.querySelectorAll('.granova-benefits-70-3903').forEach((el) => {
      if (!el.classList.contains('pa-stagger-children')) {
        el.classList.add('pa-stagger-children');
        paObserver.observe(el);
      }
    });

    /* Standalone custom liquid wrappers — fade up if not already animated by theme */
    root.querySelectorAll('.m-custom-liquid').forEach((el) => {
      if (!el.classList.contains('m-scroll-trigger') && !el.classList.contains('pa-fade-up')) {
        el.classList.add('pa-fade-up');
        paObserver.observe(el);
      }
    });
  }

  /* ============================================================
     3. WORD SPLIT REVEAL
     Applied to .m-section__heading elements that do NOT already
     have the theme's m-scroll-trigger class (to avoid conflicts).
     Each word is wrapped in:
       <span class="pa-word">
         <span class="pa-word-inner" style="--pa-i:N">word</span>
       </span>
     ============================================================ */

  function initWordSplit(root) {
    root = root || document;

    root
      .querySelectorAll('.m-section__heading:not(.m-scroll-trigger):not(.pa-word-reveal-done)')
      .forEach((heading) => {
        /* Skip if the heading contains child elements (icons, links, etc.) */
        const hasChildElements =
          Array.from(heading.childNodes).some(
            (node) => node.nodeType === Node.ELEMENT_NODE
          );
        if (hasChildElements) return;

        const text = heading.textContent.trim();
        if (!text) return;

        const words = text.split(/\s+/);

        /* Build wrapped HTML */
        const html = words
          .map(
            (word, i) =>
              `<span class="pa-word"><span class="pa-word-inner" style="--pa-i:${i}">${escapeHtml(word)}</span></span>`
          )
          .join('&nbsp;');

        heading.innerHTML = html;
        heading.classList.add('pa-word-reveal', 'pa-word-reveal-done');
        paObserver.observe(heading);
      });
  }

  /** Minimal HTML escape for word text */
  function escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /* ============================================================
     4. BUTTON RIPPLE EFFECT
     Creates an absolutely-positioned ripple span at the exact
     pointer coordinates on every .m-button press.
     ============================================================ */

  function initButtonRipple() {
    document.addEventListener('pointerdown', (e) => {
      const button = e.target.closest('.m-button');
      if (!button) return;

      /* Skip icon-only buttons where ripple looks odd */
      if (button.classList.contains('m-button--icon')) return;

      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 1.6;
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top  - size / 2;

      const ripple = document.createElement('span');
      ripple.classList.add('pa-ripple');
      ripple.style.cssText =
        `width:${size}px;height:${size}px;left:${x}px;top:${y}px;`;

      button.appendChild(ripple);

      ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
    });
  }

  /* ============================================================
     5. CART DRAWER STAGGER
     Watches the cart drawer for open/close state changes.
     When the drawer opens, staggers each cart line item by
     setting --pa-i and adding .pa-animate.
     ============================================================ */

  function initCartDrawerStagger() {
    const cartDrawer = document.querySelector('.m-cart-drawer');
    if (!cartDrawer) return;

    function staggerItems() {
      const container = cartDrawer.querySelector('.m-cart-drawer__items');
      if (!container) return;

      Array.from(container.children).forEach((item, i) => {
        /* Reset so animation replays if items change */
        item.classList.remove('pa-cart-item-stagger', 'pa-animate');
        item.style.setProperty('--pa-i', i);

        /* Force reflow so transition triggers */
        void item.offsetWidth;

        item.classList.add('pa-cart-item-stagger');
        nextFrame(() => item.classList.add('pa-animate'));
      });
    }

    /* Watch aria-hidden toggle — Minimog opens/closes via this attribute */
    const attrObserver = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.attributeName === 'aria-hidden') {
          const isOpen =
            cartDrawer.getAttribute('aria-hidden') === 'false' ||
            cartDrawer.classList.contains('is-open');
          if (isOpen) {
            /* Delay slightly so the drawer slide animation has started */
            setTimeout(staggerItems, 120);
          }
        }
      });
    });

    attrObserver.observe(cartDrawer, { attributes: true });

    /* Also re-stagger when items are added or removed from the list */
    const itemsContainer = cartDrawer.querySelector('.m-cart-drawer__items');
    if (itemsContainer) {
      const childObserver = new MutationObserver(() => {
        const isOpen =
          cartDrawer.getAttribute('aria-hidden') === 'false' ||
          cartDrawer.classList.contains('is-open');
        if (isOpen) setTimeout(staggerItems, 80);
      });
      childObserver.observe(itemsContainer, { childList: true });
    }
  }

  /* ============================================================
     6. SKELETON LOADING
     Adds .pa-skeleton (shimmer) to the responsive-image / .m-image
     wrapper while the contained <img> is still loading.
     Removed immediately on load or error.
     ============================================================ */

  function applySkeletonToImage(img) {
    /* Already loaded — nothing to do */
    if (img.complete && img.naturalWidth > 0) return;

    const wrapper = img.closest('responsive-image, .m-image');
    if (!wrapper) return;

    wrapper.classList.add('pa-skeleton');

    const removeSkeleton = () => wrapper.classList.remove('pa-skeleton');

    img.addEventListener('load',  removeSkeleton, { once: true });
    img.addEventListener('error', removeSkeleton, { once: true });
  }

  function initSkeletonLoading(root) {
    root = root || document;
    root.querySelectorAll('responsive-image img, .m-image img').forEach(applySkeletonToImage);
  }

  /* ============================================================
     7. SVG PATH DRAW
     .pa-draw-svg elements are observed by paObserver (section 1).
     No extra initialization needed here — path lengths are read
     inside the observer callback when the element enters view.
     ============================================================ */

  /* ============================================================
     8. THEME EDITOR SUPPORT
     Re-initialize in sections that are live-previewed in the
     Shopify Customizer so animations still work during editing.
     ============================================================ */

  function onSectionLoad(event) {
    const section = event.target;
    observePaElements(section);
    autoApplyClasses(section);
    initWordSplit(section);
    initSkeletonLoading(section);
  }

  /* ============================================================
     INITIALIZE ALL MODULES
     ============================================================ */

  function init() {
    observePaElements();
    autoApplyClasses();
    initWordSplit();
    initButtonRipple();
    initCartDrawerStagger();
    initSkeletonLoading();

    /* Shopify Theme Editor integration */
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', onSectionLoad);
      document.addEventListener('shopify:section:reorder', () => {
        observePaElements();
        autoApplyClasses();
      });
    }
  }

  onReady(init);
})();
